import React, { useEffect, useState } from "react"
import { Button, Spinner } from "reactstrap"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { ROUTES } from "constants/routes"
import { useOnboarding } from "context/OnboardingContext"
import {
  onboardingGet,
  hasOnboardingRegistration,
  getOnboardingApiMessage,
  ONBOARDING_PLAN_KEY,
  ONBOARDING_PLAN_SNAPSHOT_KEY,
} from "helpers/onboarding_api_helper"
import OnboardingLayout from "./OnboardingLayout"
import PlanDetailsModal from "./PlanDetailsModal"

const SelectPlan = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    selectedPlanId,
    setSelectedPlanId,
    setPlanSnapshot,
  } = useOnboarding()
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [detailsPlan, setDetailsPlan] = useState(null)

  useEffect(() => {
    document.title = `${t("Choose a plan")} | Wulooj`

    if (!hasOnboardingRegistration()) {
      navigate(ROUTES.REGISTER, { replace: true })
      return
    }

    const storedPlanId = sessionStorage.getItem(ONBOARDING_PLAN_KEY)
    if (storedPlanId && !selectedPlanId) {
      setSelectedPlanId(storedPlanId)
    }

    let cancelled = false
    onboardingGet("onboarding/plans")
      .then(res => {
        if (cancelled) return
        if (res?.status) {
          setPlans(Array.isArray(res.data) ? res.data : [])
        } else {
          setError(
            getOnboardingApiMessage(res) || t("Unable to load plans.")
          )
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(t("Unable to load plans."))
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  const handleContinue = () => {
    if (!selectedPlanId) {
      setError(t("Please select a plan."))
      return
    }
    const plan = plans.find(p => p._id === selectedPlanId)
    if (plan) {
      setPlanSnapshot(plan)
      sessionStorage.setItem(ONBOARDING_PLAN_KEY, String(plan._id))
      sessionStorage.setItem(ONBOARDING_PLAN_SNAPSHOT_KEY, JSON.stringify(plan))
    }
    navigate(ROUTES.REGISTER_PAYMENT)
  }

  const handleSelectPlan = plan => {
    setSelectedPlanId(plan._id)
    setPlanSnapshot(plan)
    setError("")
  }

  const handleOpenDetails = (e, plan) => {
    e.stopPropagation()
    setDetailsPlan(plan)
  }

  return (
    <OnboardingLayout>
      <div className="onboarding-card onboarding-card--scrollable">
        <div className="onboarding-steps">
          <span className="step-pill done">{t("1. Business details")}</span>
          <span className="step-pill active">{t("2. Choose plan")}</span>
          <span className="step-pill">{t("3. Payment")}</span>
        </div>

        <h4 className="mb-1">{t("Select your subscription plan")}</h4>
        <p className="text-muted mb-3">
          {t("Choose the plan that best fits your needs.")}
        </p>

        {error && <div className="alert alert-danger">{error}</div>}

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : plans.length === 0 ? (
          <p className="text-muted text-center py-4">
            {t("No plans available at the moment.")}
          </p>
        ) : (
          <div className="onboarding-plan-scroll-area">
            <div className="onboarding-plan-grid">
              {plans.map(plan => (
                <div
                  key={plan._id}
                  className={`onboarding-plan-card${
                    selectedPlanId === plan._id ? " selected" : ""
                  }`}
                  onClick={() => handleSelectPlan(plan)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleSelectPlan(plan)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <h5 className="onboarding-plan-card__name fw-bold mb-2">
                    {plan.name}
                  </h5>
                  {plan.description && (
                    <p className="onboarding-plan-card__desc text-muted small mb-3">
                      {plan.description}
                    </p>
                  )}
                  <div className="plan-price">
                    {plan.currency || "SAR"} {Number(plan.price || 0).toFixed(2)}
                    <span className="text-muted fs-6 fw-normal">
                      {" / "}
                      {plan.billingCycle === "annual" || plan.billingCycle === "yearly"
                        ? t("year")
                        : t("month")}
                    </span>
                  </div>
                  <Button
                    color="link"
                    className="onboarding-plan-details-btn p-0 mt-2"
                    onClick={e => handleOpenDetails(e, plan)}
                  >
                    {t("View details")}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <PlanDetailsModal
          plan={detailsPlan}
          isOpen={!!detailsPlan}
          onClose={() => setDetailsPlan(null)}
        />

        <div className="d-flex gap-2 onboarding-card-footer">
          <Button color="primary" onClick={handleContinue} disabled={!selectedPlanId}>
            {t("Continue to payment")}
          </Button>
          <Button
            color="secondary"
            outline
            type="button"
            onClick={() => navigate(ROUTES.REGISTER)}
          >
            {t("Back")}
          </Button>
        </div>
      </div>
    </OnboardingLayout>
  )
}

export default SelectPlan
